import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDashboardCache } from '../../dashboard/context/DashboardContext';
import { attendanceApi } from '../services/api';
import empIcon from '../../../assets/Layer_2.png';
import presentIcon from '../../../assets/Group 307.png';
import absentIcon from '../../../assets/Group 308.png';
import prevIcon from '../../../assets/Group 309.png';
import { BarChart3, Building2, ChevronRight, TrendingUp, GitCompare, CalendarCheck } from 'lucide-react';
import KVSLoader from '../../../components/loaders/KVSLoader';
import ReactECharts from 'echarts-for-react';

// Types
type BreakdownItem = { label: string; value: string };
type StatCard = { label: string; value: string; breakdown: BreakdownItem[]; icon: string; color?: string; iconColor?: string };

const initialStatData: StatCard[] = [
  { label: "TOTAL STUDENTS", value: '--', breakdown: [{ label: 'Boys', value: '--' }, { label: 'Girls', value: '--' }], icon: empIcon, color: 'bg-[#fff]', iconColor: '#57C3F2' },
  { label: 'PRESENT TODAY', value: '--', breakdown: [{ label: 'Boys', value: '--' }, { label: 'Girls', value: '--' }], icon: presentIcon, color: 'bg-[#fff]', iconColor: '#279E15' },
  { label: 'ABSENT TODAY', value: '--', breakdown: [{ label: 'Boys', value: '--' }, { label: 'Girls', value: '--' }], icon: absentIcon, color: 'bg-[#FFF]', iconColor: '#FF0000' },
  { label: 'ATTENDANCE %', value: '--', breakdown: [{ label: 'Boys', value: '--' }, { label: 'Girls', value: '--' }], icon: prevIcon, color: 'bg-[#fff]', iconColor: '#E4B124' },
];

export default function StudentAttendanceDashboard() {
  const navigate = useNavigate();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { studentAttendanceData: _studentAttendanceData, setCachedStudentAttendance } = useDashboardCache();
  const [studentStats, setStudentStats] = useState<any>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_isLoading, setIsLoading] = useState(true);
  const [classWiseData, setClassWiseData] = useState<any[]>([]);
  const [loadingClassData, setLoadingClassData] = useState(true);
  const [todayClassData, setTodayClassData] = useState<any[]>([]);
  const [loadingTodayData, setLoadingTodayData] = useState(true);
  const [todaysOverview, setTodaysOverview] = useState<any>(null);

  // Get current date in YYYY-MM-DD format
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const fallbackDate = getTodayDate();
  const now = new Date();

  // Month/Year state for trend chart
  const [selectedMonth, setSelectedMonth] = useState<number>(now.getMonth()); // 0-indexed
  const [selectedYear, setSelectedYear] = useState<number>(now.getFullYear());

  // Date state for today's chart
  const [selectedDate, setSelectedDate] = useState<string>(fallbackDate);

  // Compute date range based on selected month/year
  const getMonthDateRange = (month: number, year: number) => {
    const today = new Date();
    const isCurrentMonth = month === today.getMonth() && year === today.getFullYear();
    const fromDate = new Date(year, month, 1).toISOString().split('T')[0];
    const toDate = isCurrentMonth
      ? today.toISOString().split('T')[0]
      : new Date(year, month + 1, 0).toISOString().split('T')[0]; // last day of month
    return { fromDate, toDate };
  };

  const dateRange = getMonthDateRange(selectedMonth, selectedYear);

  // Generate list of available years
  const availableYears = Array.from({ length: now.getFullYear() - 2022 }, (_, i) => 2023 + i);

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  // Helper function to get class display name
  const getClassName = (classNum: number): string => {
    switch (classNum) {
      case -3: return 'Balvatika I';
      case -2: return 'Balvatika II';
      case -1: return 'Balvatika III';
      default: return `Class ${classNum}`;
    }
  };

  // Helper function to sort classes (Balvatika first, then 1-12)
  const sortClasses = (a: any, b: any): number => {
    // Balvatika classes come first (-3, -2, -1)
    if (a.class < 0 && b.class < 0) return a.class - b.class; // -3 before -2 before -1
    if (a.class < 0) return -1; // Balvatika before regular classes
    if (b.class < 0) return 1;
    return a.class - b.class; // Regular classes in order
  };

  const statData = useMemo(() => {
    const result = [...initialStatData];

    // Use todaysOverview summary for main totals, studentStats for Boys/Girls breakdown
    const overview = todaysOverview;
    const breakdown = studentStats;

    if (overview || breakdown) {
      // Total Students
      result[0] = {
        ...result[0],
        value: (overview?.totalEnrollment ?? breakdown?.totalEnrollment ?? 0).toLocaleString(),
        breakdown: [
          { label: 'Boys', value: (breakdown?.totalboysEnrolled || 0).toLocaleString() },
          { label: 'Girls', value: (breakdown?.totalgirlsEnrolled || 0).toLocaleString() }
        ]
      };

      // Present Today
      result[1] = {
        ...result[1],
        value: (overview?.totalPresent ?? breakdown?.totalPresent ?? 0).toLocaleString(),
        breakdown: [
          { label: 'Boys', value: (breakdown?.totalBoysPresent || 0).toLocaleString() },
          { label: 'Girls', value: (breakdown?.totalGirlsPresent || 0).toLocaleString() }
        ]
      };

      // Absent Today
      result[2] = {
        ...result[2],
        value: (overview?.totalAbsent ?? breakdown?.totalAbsent ?? 0).toLocaleString(),
        breakdown: [
          { label: 'Boys', value: (breakdown?.totalBoysAbsent || 0).toLocaleString() },
          { label: 'Girls', value: (breakdown?.totalGirlsAbsent || 0).toLocaleString() }
        ]
      };

      // Attendance Percentage
      const pct = overview?.overallAttendancePercentage ?? breakdown?.totalAttendancePercentage ?? 0;
      result[3] = {
        ...result[3],
        value: `${Number(pct).toFixed(1)}%`,
        breakdown: [
          { label: 'Boys', value: `${(breakdown?.boysPresentPercentage || 0).toFixed(1)}%` },
          { label: 'Girls', value: `${(breakdown?.girlsPresentPercentage || 0).toFixed(1)}%` }
        ]
      };
    }

    return result;
  }, [todaysOverview, studentStats]);

  useEffect(() => {
    // Fetch today's detailed attendance summary (region-wise totals)
    const loadTodaysOverview = async () => {
      try {
        const response = await attendanceApi.getTodaysDetailedAttendance();
        if (response?.success && response.summary) {
          setTodaysOverview(response.summary);
          setCachedStudentAttendance(response.summary.totalEnrollment || 0);
        }
      } catch {
        // silently fall back to class-wise data
      }
    };

    // Fetch class-wise breakdown for Boys/Girls splits
    const loadStudentData = async () => {
      setIsLoading(true);
      try {
        const response = await attendanceApi.getTotalClassWiseSectionData(
          undefined,
          fallbackDate,
          fallbackDate
        );
        if (response?.success && response.data) {
          setStudentStats(response.data);
          // Only cache enrollment if todaysOverview hasn't set it yet
          if (!todaysOverview) {
            setCachedStudentAttendance(response.data.totalEnrollment || 0);
          }
        }
      } catch {
        // ignore
      } finally {
        setIsLoading(false);
      }
    };

    loadTodaysOverview();
    loadStudentData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setCachedStudentAttendance]);

  // Fetch class-wise data for the chart
  useEffect(() => {
    const loadClassWiseData = async () => {
      setLoadingClassData(true);
      try {
        // Fetch all pages of class-wise data
        const allClasses: any[] = [];
        let currentPage = 1;
        let hasMore = true;

        while (hasMore) {
          const response = await attendanceApi.getClassWiseData(
            currentPage,
            dateRange.fromDate,
            dateRange.toDate
          );

          if (response?.success && Array.isArray(response.data)) {
            allClasses.push(...response.data);

            // Check if there are more pages
            if (response.pagination) {
              hasMore = currentPage < response.pagination.totalPages;
              currentPage++;
            } else {
              hasMore = false;
            }
          } else {
            hasMore = false;
          }
        }

        // Include Balvatika (-3, -2, -1) and regular classes (1-12)
        const validClasses = allClasses
          .filter((c: any) => (c.class >= -3 && c.class <= -1) || (c.class >= 1 && c.class <= 12))
          .sort(sortClasses);

        // console.log('Total classes fetched:', allClasses.length);
        // console.log('Valid classes after filter:', validClasses.length);
        // console.log('Class numbers:', validClasses.map(c => c.class));

        setClassWiseData(validClasses);
      } catch (error) {
        // console.error('Error loading class-wise data:', error);
        setClassWiseData([]);
      } finally {
        setLoadingClassData(false);
      }
    };

    loadClassWiseData();
  }, [dateRange.fromDate, dateRange.toDate]);

  // Fetch class data for selected date (today's chart)
  useEffect(() => {
    const loadSelectedDateData = async () => {
      setLoadingTodayData(true);
      try {
        const response = await attendanceApi.getClassWiseData(
          1,
          selectedDate,
          selectedDate
        );

        if (response?.success && Array.isArray(response.data)) {
          const validClasses = response.data
            .filter((c: any) => (c.class >= -3 && c.class <= -1) || (c.class >= 1 && c.class <= 12))
            .sort(sortClasses);
          setTodayClassData(validClasses);
        } else {
          setTodayClassData([]);
        }
      } catch (error) {
        setTodayClassData([]);
      } finally {
        setLoadingTodayData(false);
      }
    };

    loadSelectedDateData();
  }, [selectedDate]);

  const handleStatCardClick = (statLabel: string) => {
    // Navigate to regions page for analytics
    navigate('/attendance/regions');
  };

  // Create line chart options for class-wise trend
  const classWiseTrendOptions = useMemo(() => {
    if (!classWiseData.length) return {};

    const classes = classWiseData.map(c => getClassName(c.class));
    const attendanceData = classWiseData.map(c => c.totalAttendancePercentage || 0);
    const boysData = classWiseData.map(c => c.boysPresentPercentage || 0);
    const girlsData = classWiseData.map(c => c.girlsPresentPercentage || 0);

    return {
      title: {
        text: `Class-wise Attendance Trend — ${monthNames[selectedMonth]} ${selectedYear} (${dateRange.fromDate} to ${dateRange.toDate})`,
        left: 'center',
        textStyle: { fontSize: 13, fontWeight: 600, color: '#374151' }
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'cross', animation: true },
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: '#e5e7eb',
        borderWidth: 1,
      },
      legend: {
        data: ['Overall', 'Boys', 'Girls'],
        bottom: 10,
        textStyle: { fontSize: 11 }
      },
      grid: {
        left: 60,
        right: 20,
        top: 60,
        bottom: 100,
      },
      dataZoom: [
        {
          type: 'slider',
          show: true,
          xAxisIndex: [0],
          start: 0,
          end: 100,
          bottom: 60,
          height: 20,
          handleSize: '80%',
          textStyle: { fontSize: 10 },
        },
        {
          type: 'inside',
          xAxisIndex: [0],
          start: 0,
          end: 100,
        }
      ],
      xAxis: {
        type: 'category',
        data: classes,
        axisLabel: {
          fontSize: 10,
          color: '#6b7280'
        },
        axisLine: { lineStyle: { color: '#e5e7eb' } },
      },
      yAxis: {
        type: 'value',
        name: 'Attendance %',
        min: 0,
        max: 100,
        axisLabel: {
          formatter: '{value}%',
          fontSize: 10,
          color: '#6b7280'
        },
        splitLine: { lineStyle: { color: '#f3f4f6', type: 'dashed' } },
      },
      series: [
        {
          name: 'Overall',
          type: 'line',
          data: attendanceData,
          smooth: true,
          lineStyle: { width: 3, color: '#6A1786' },
          itemStyle: { color: '#6A1786' },
          areaStyle: {
            color: {
              type: 'linear',
              x: 0, y: 0, x2: 0, y2: 1,
              colorStops: [
                { offset: 0, color: 'rgba(106, 23, 134, 0.3)' },
                { offset: 1, color: 'rgba(106, 23, 134, 0.05)' }
              ]
            }
          },
          symbol: 'circle',
          symbolSize: 6,
        },
        {
          name: 'Boys',
          type: 'line',
          data: boysData,
          smooth: true,
          lineStyle: { width: 2, color: '#3b82f6' },
          itemStyle: { color: '#3b82f6' },
          symbol: 'circle',
          symbolSize: 5,
        },
        {
          name: 'Girls',
          type: 'line',
          data: girlsData,
          smooth: true,
          lineStyle: { width: 2, color: '#ec4899' },
          itemStyle: { color: '#ec4899' },
          symbol: 'circle',
          symbolSize: 5,
        }
      ],
      animation: true,
      animationDuration: 800,
    };
  }, [classWiseData]);

  // Create line chart options for today's class statistics
  const todayClassOptions = useMemo(() => {
    if (!todayClassData.length) return {};

    const classes = todayClassData.map(c => getClassName(c.class));
    const enrollment = todayClassData.map(c => c.totalEnrollment || 0);
    const present = todayClassData.map(c => c.totalPresent || 0);
    const absent = todayClassData.map(c => c.totalAbsent || 0);

    return {
      title: {
        text: `Class Statistics — ${selectedDate}`,
        left: 'center',
        textStyle: { fontSize: 14, fontWeight: 600, color: '#374151' }
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'cross' },
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: '#e5e7eb',
        borderWidth: 1,
      },
      legend: {
        data: ['Enrollment', 'Present', 'Absent'],
        bottom: 10,
        textStyle: { fontSize: 11 }
      },
      grid: {
        left: 60,
        right: 20,
        top: 60,
        bottom: 100,
      },
      dataZoom: [
        {
          type: 'slider',
          show: true,
          xAxisIndex: [0],
          start: 0,
          end: 100,
          bottom: 60,
          height: 20,
          handleSize: '80%',
          textStyle: { fontSize: 10 },
        },
        {
          type: 'inside',
          xAxisIndex: [0],
          start: 0,
          end: 100,
        }
      ],
      xAxis: {
        type: 'category',
        data: classes,
        axisLabel: {
          fontSize: 10,
          color: '#6b7280'
        },
        axisLine: { lineStyle: { color: '#e5e7eb' } },
      },
      yAxis: {
        type: 'value',
        name: 'Students',
        axisLabel: {
          formatter: '{value}',
          fontSize: 10,
          color: '#6b7280'
        },
        splitLine: { lineStyle: { color: '#f3f4f6', type: 'dashed' } },
      },
      series: [
        {
          name: 'Enrollment',
          type: 'line',
          data: enrollment,
          smooth: true,
          lineStyle: { width: 2.5, color: '#57C3F2' },
          itemStyle: { color: '#57C3F2' },
          symbol: 'circle',
          symbolSize: 6,
        },
        {
          name: 'Present',
          type: 'line',
          data: present,
          smooth: true,
          lineStyle: { width: 2.5, color: '#279E15' },
          itemStyle: { color: '#279E15' },
          areaStyle: {
            color: {
              type: 'linear',
              x: 0, y: 0, x2: 0, y2: 1,
              colorStops: [
                { offset: 0, color: 'rgba(39, 158, 21, 0.2)' },
                { offset: 1, color: 'rgba(39, 158, 21, 0.05)' }
              ]
            }
          },
          symbol: 'circle',
          symbolSize: 6,
        },
        {
          name: 'Absent',
          type: 'line',
          data: absent,
          smooth: true,
          lineStyle: { width: 2, color: '#FF0000' },
          itemStyle: { color: '#FF0000' },
          symbol: 'circle',
          symbolSize: 5,
        }
      ],
      animation: true,
      animationDuration: 800,
    };
  }, [todayClassData, selectedDate]);

  return (
    <div className="min-h-screen w-full px-4 md:px-10 lg:px-20 py-8">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <h2 className="text-2xl font-bold text-[#6A1786]">Student Attendance</h2>
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/attendance/student/todays-overview')}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 text-gray-700 font-medium rounded-lg shadow-sm hover:shadow hover:border-purple-300 transition-all"
          >
            <CalendarCheck className="w-4 h-4 text-[#6A1786]" />
            <span className="hidden sm:inline">Today's Overview</span>
            <ChevronRight className="w-4 h-4" />
          </button>
          <button
            onClick={() => navigate('/attendance/regions')}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 text-gray-700 font-medium rounded-lg shadow-sm hover:shadow hover:border-purple-300 transition-all"
          >
            <Building2 className="w-4 h-4" />
            <span className="hidden sm:inline">Regions</span>
            <ChevronRight className="w-4 h-4" />
          </button>
          {/* <button
            onClick={() => navigate('/attendance/student/analytics')}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 text-gray-700 font-medium rounded-lg shadow-sm hover:shadow hover:border-purple-300 transition-all"
          >
            <Building2 className="w-4 h-4" />
            <span className="hidden sm:inline">KV-wise List</span>
            <ChevronRight className="w-4 h-4" />
          </button> */}
          <button
            onClick={() => navigate('/attendance/student/analytics/compare')}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-medium rounded-lg shadow-sm hover:shadow-lg transition-all"
          >
            <BarChart3 className="w-4 h-4" />
            <span className="hidden sm:inline">Analytics Dashboard</span>
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Analytics Banner */}
      <div
        onClick={() => navigate('/attendance/regions')}
        className="mb-6 p-4 bg-gradient-to-r from-purple-50 via-blue-50 to-indigo-50 border border-purple-200 rounded-xl cursor-pointer hover:shadow-md transition-shadow"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white rounded-lg shadow-sm">
              <BarChart3 className="w-6 h-6 text-[#6A1786]" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">📊 Student Attendance Analytics & Comparison Dashboard</h3>
              <p className="text-sm text-gray-600">
                Compare trends across Regions • KV Schools • Classes • Boys vs Girls performance tracking
              </p>
            </div>
          </div>
          <div className="hidden md:flex items-center gap-2">
            <div className="flex items-center gap-1 px-3 py-1 bg-white rounded-full text-xs font-medium text-purple-700 shadow-sm">
              <TrendingUp className="w-3 h-3" />
              Trends
            </div>
            <div className="flex items-center gap-1 px-3 py-1 bg-white rounded-full text-xs font-medium text-blue-700 shadow-sm">
              <GitCompare className="w-3 h-3" />
              Compare
            </div>
            <ChevronRight className="w-5 h-5 text-purple-600" />
          </div>
        </div>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 2xl:grid-cols-4 gap-4 mb-8">
        {statData.map((stat, idx) => (
          <button
            key={idx}
            onClick={() => handleStatCardClick(stat.label)}
            className={`flex items-center gap-2 sm:gap-3 md:gap-4 rounded-lg shadow bg-white p-2 sm:p-3 md:p-4 border-t-4 ${stat.iconColor} text-left hover:shadow-lg transition-shadow min-h-[80px] sm:min-h-[92px]`}
            style={{ cursor: 'pointer', borderTopColor: stat.iconColor }}
            aria-label={`Open student attendance details for ${stat.label}`}
          >
            <div
              className="flex items-center justify-center flex-shrink-0"
              style={{
                width: 'clamp(40px, 10vw, 56px)',
                height: 'clamp(40px, 10vw, 56px)',
                background: stat.iconColor,
                borderRadius: 10
              }}
            >
              <img src={stat.icon} alt={stat.label} style={{ width: '60%', height: '60%' }} className="flex-shrink-0 object-contain" />
            </div>
            <div className="flex-1 flex flex-col md:flex-row items-start md:items-center justify-between gap-2 md:gap-4 min-w-0">
              <div className="flex flex-col min-w-0 flex-1">
                <div className="text-[10px] sm:text-xs font-semibold text-gray-500 mb-1 truncate">{stat.label}</div>
                <div className="text-sm sm:text-base md:text-lg lg:text-xl font-bold text-[#6A1786] mb-1 truncate">{stat.value}</div>
              </div>

              {/* Vertical divider for larger screens */}
              <div className="hidden md:block h-10 border-l border-gray-200" />

              {/* Breakdown on right */}
              <div className="flex flex-col items-end ml-0 md:ml-2 flex-shrink-0">
                {stat.breakdown && Array.isArray(stat.breakdown) && (
                  stat.breakdown.map((item: BreakdownItem, i: number) => (
                    <div key={i} className="flex items-center gap-1 sm:gap-2 whitespace-nowrap">
                      <span className="text-[9px] sm:text-[10px] md:text-xs text-gray-600 font-medium">{item.label}</span>
                      <span className="text-[10px] sm:text-xs md:text-sm text-[#57C3F2] font-semibold">{item.value}</span>
                    </div>
                  ))
                )}
              </div>
            </div>
          </button>
        ))}
      </div>

      {/* Line Charts */}

        {/* Trend Chart Controls */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <h3 className="text-base font-semibold text-gray-700">Class-wise Attendance Trend</h3>
          <div className="flex items-center gap-2">
            <select
              value={selectedMonth}
              onChange={e => setSelectedMonth(Number(e.target.value))}
              className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 bg-white text-gray-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-300"
            >
              {monthNames.map((name, idx) => (
                <option key={idx} value={idx} disabled={selectedYear === now.getFullYear() && idx > now.getMonth()}>
                  {name}
                </option>
              ))}
            </select>
            <select
              value={selectedYear}
              onChange={e => setSelectedYear(Number(e.target.value))}
              className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 bg-white text-gray-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-300"
            >
              {availableYears.map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          {loadingClassData ? (
            <div className="min-h-[320px] flex flex-col items-center justify-center gap-3">
              <KVSLoader size={72} />
              <span className="text-sm text-gray-400">Loading class-wise data...</span>
            </div>
          ) : classWiseData.length === 0 ? (
            <div className="min-h-[320px] flex items-center justify-center text-gray-400">
              No class-wise data available
            </div>
          ) : (
            <ReactECharts
              option={classWiseTrendOptions}
              style={{ height: '450px', width: '100%' }}
              opts={{ renderer: 'svg' }}
            />
          )}
        </div>
        {/* Today's Chart Controls */}
        <div className="flex flex-wrap items-center justify-between gap-3 mt-6 mb-3">
          <h3 className="text-base font-semibold text-gray-700">Class Statistics by Date</h3>
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600 font-medium">Select Date:</label>
            <input
              type="date"
              value={selectedDate}
              max={fallbackDate}
              onChange={e => setSelectedDate(e.target.value)}
              className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 bg-white text-gray-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-300"
            />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          {loadingTodayData ? (
            <div className="min-h-[320px] flex flex-col items-center justify-center gap-3">
              <KVSLoader size={72} />
              <span className="text-sm text-gray-400">Loading data...</span>
            </div>
          ) : todayClassData.length === 0 ? (
            <div className="min-h-[320px] flex items-center justify-center text-gray-400">
              No data available for today
            </div>
          ) : (
            <ReactECharts
              option={todayClassOptions}
              style={{ height: '450px', width: '100%' }}
              opts={{ renderer: 'svg' }}
            />
          )}
        </div>
      </div>
  
  );
}